import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { stateOfLineage } from '@/components/fields/lineage'
import { dealFileService } from '@/services/dealFile'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { loansService } from '@/services/loans'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'

/**
 * Whether a person has confirmed a deal's figures, field by field.
 *
 * Read from the deal's lineage — the same read the Inspector and the Audit page
 * already make, so asking costs no extra request — because the platform already
 * says, for every figure, whether a person set or accepted it (`confirmed`) or
 * the engine, a document or a default did (`stated` / `suggested` / `derived`).
 * A figure is PENDING until a person confirms it. A machine write is never
 * read as a confirmation: auto-promotion, the derivation engine and the
 * geocoder are all the platform, whatever word their event carries.
 *
 * `derived` is neither: the platform computed it, and what a person confirms is
 * its inputs. It is reported on its own so a surface never nags for a
 * confirmation the server would refuse.
 */

export type FieldConfirmationStatus =
  | 'confirmed'
  | 'pending'
  | 'derived'
  | 'conflicted'
  | 'missing'
  | 'not_applicable'

export interface FieldConfirmation {
  status: FieldConfirmationStatus
  /** Who confirmed it, when confirmed. */
  by: string | null
  /** When they confirmed it. */
  at: string | null
  /** Where the value came from, in one line — the pending mark's hover. */
  basis: string | null
}

/** One lineage row's confirmation status. Pure, so a test can pin the rule. */
export function confirmationOfLineage(field: LoanFieldLineageField | undefined | null): FieldConfirmation {
  const state = stateOfLineage(field, 'missing')
  const basis = field?.summary ?? null
  switch (state) {
    case 'confirmed':
      return {
        status: 'confirmed',
        by: field?.confirmation?.by ?? null,
        at: field?.confirmation?.at ?? null,
        basis,
      }
    case 'stated':
    case 'suggested':
      return { status: 'pending', by: null, at: null, basis }
    case 'derived':
      return { status: 'derived', by: null, at: null, basis }
    case 'conflicted':
      return { status: 'conflicted', by: null, at: null, basis }
    case 'not_applicable':
      return { status: 'not_applicable', by: null, at: null, basis }
    default:
      return { status: 'missing', by: null, at: null, basis }
  }
}

export interface FieldConfirmationCounts {
  confirmed: number
  pending: number
  derived: number
  conflicted: number
  missing: number
}

export function countConfirmations(
  fields: LoanFieldLineageField[] | undefined,
  /** The catalogue's field names. The lineage also tracks columns the catalogue
   *  does not offer (the borrower mirror, kill fields); a count that included
   *  them would not match the Confirmation view's. */
  catalogue?: ReadonlySet<string> | null,
): FieldConfirmationCounts {
  const counts: FieldConfirmationCounts = { confirmed: 0, pending: 0, derived: 0, conflicted: 0, missing: 0 }
  for (const field of fields ?? []) {
    if (catalogue && !catalogue.has(field.fieldName)) continue
    const { status } = confirmationOfLineage(field)
    if (status === 'not_applicable') continue
    counts[status] += 1
  }
  return counts
}

export interface UseFieldConfirmation {
  isLoading: boolean
  /** One field's status, or undefined until the lineage has loaded. */
  of: (field: string) => FieldConfirmation | undefined
  /** True only for a present value no person has confirmed. */
  isPending: (field: string) => boolean
  /** The pending mark's `basis` for a field, or null when it is not pending. */
  pendingBasis: (field: string) => string | null
  counts: FieldConfirmationCounts
}

export function useFieldConfirmation(
  loanId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UseFieldConfirmation {
  // The Inspector and the Audit page read this exact key, so a deal that is
  // already open costs nothing.
  const lineage = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId ?? ''),
    queryFn: () => loansService.getFieldLineage(loanId!),
    enabled: enabled && Boolean(loanId),
  })

  // The field catalogue, shared with the dossier and Manual Entry. Without it
  // (a refusal, say) the counts still read, over every field the lineage tracks.
  const catalogue = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    enabled: enabled && Boolean(loanId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const catalogueNames = useMemo(
    () => (catalogue.data?.fields ? new Set(catalogue.data.fields.map((f) => f.name)) : null),
    [catalogue.data?.fields],
  )

  const byField = useMemo(() => {
    const map = new Map<string, FieldConfirmation>()
    for (const field of lineage.data?.fields ?? []) {
      map.set(field.fieldName, confirmationOfLineage(field))
    }
    return map
  }, [lineage.data?.fields])

  const of = useCallback((field: string) => byField.get(field), [byField])
  const isPending = useCallback((field: string) => byField.get(field)?.status === 'pending', [byField])
  const pendingBasis = useCallback(
    (field: string) => {
      const entry = byField.get(field)
      return entry?.status === 'pending' ? (entry.basis ?? '') : null
    },
    [byField],
  )
  const counts = useMemo(
    () => countConfirmations(lineage.data?.fields, catalogueNames),
    [lineage.data?.fields, catalogueNames],
  )

  return {
    // Counts wait for the catalogue (or its failure) so they do not read one
    // number and then another.
    isLoading: lineage.isLoading || catalogue.isLoading,
    of,
    isPending,
    pendingBasis,
    counts,
  }
}
